import { useCallback, useState } from 'react';

/**
 * Gate a bulk-export action through a confirmation dialog when the user has
 * a non-zero download limit. Users without a limit (`downloads` is null or
 * `downloads.limit <= 0`) bypass the dialog and hit `onDownload` directly —
 * a `limit <= 0` means "unlimited" per subscription utility convention.
 *
 * While subscription status is still loading, `requestBulkExport` is a no-op
 * (prevents a silent direct-download fire while we don't yet know whether
 * the dialog should show).
 *
 * Returns state + four stable callbacks:
 *   - requestBulkExport(type): call from button onClick. Opens dialog OR
 *     fires download depending on limit.
 *   - confirmBulkExport(): call from dialog onConfirm. Captures the pending
 *     type before clearing state, then fires download.
 *   - cancelBulkExport(): call from dialog onCancel / Escape / backdrop.
 *   - reopenForLimitExceeded(type, data): call from a host's download
 *     onError when the BE returns DOWNLOAD_LIMIT_EXCEEDED. Re-opens the
 *     dialog and seeds it with the rich envelope so the modal can show
 *     the precise shortfall (skipping the precheck round-trip).
 *
 * `bulkExportPrefillData` is the seeded envelope from a 403 retake; when
 * non-null the modal renders directly from it instead of firing a precheck.
 *
 * @param {Object} opts
 * @param {{ limit?: number } | null} opts.downloads - subscription download data
 * @param {boolean} opts.isLoading - subscription status still loading
 * @param {(type: string) => void} opts.onDownload - the actual download trigger
 */
export default function useBulkExportConfirm({ downloads, isLoading, onDownload }) {
  const [bulkExportPendingType, setBulkExportPendingType] = useState(null);
  const [bulkExportPrefillData, setBulkExportPrefillData] = useState(null);

  const requestBulkExport = useCallback(
    (type) => {
      if (isLoading) return;
      if (downloads && downloads.limit > 0) {
        setBulkExportPrefillData(null);
        setBulkExportPendingType(type);
      } else {
        onDownload(type);
      }
    },
    [downloads, isLoading, onDownload]
  );

  const confirmBulkExport = useCallback(() => {
    // Clear prefill BEFORE firing onDownload. If onDownload happens to invoke
    // its onError synchronously (e.g., a test mock that resolves the request
    // inline) → reopenForLimitExceeded → setBulkExportPrefillData(envelope),
    // we must not clobber the new envelope with a trailing setNull.
    setBulkExportPrefillData(null);
    setBulkExportPendingType((currentType) => {
      if (currentType) onDownload(currentType);
      return null;
    });
  }, [onDownload]);

  const cancelBulkExport = useCallback(() => {
    setBulkExportPendingType(null);
    setBulkExportPrefillData(null);
  }, []);

  const reopenForLimitExceeded = useCallback((type, data) => {
    setBulkExportPrefillData(data);
    setBulkExportPendingType(type);
  }, []);

  return {
    bulkExportPendingType,
    bulkExportPrefillData,
    requestBulkExport,
    confirmBulkExport,
    cancelBulkExport,
    reopenForLimitExceeded
  };
}
